import { z } from 'zod';

export type SignInRequest = z.infer<typeof $SignInRequest>;
export const $SignInRequest = z.object({
  email: z.string(),
  password: z.string(),
});

export type TokensData = z.infer<typeof $TokensData>;
export const $TokensData = z.object({
  tokenType: z.string(),
  expiresIn: z.number(),
  accessToken: z.string(),
  refreshToken: z.string(),
});

export type User = z.infer<typeof $User>;
export const $User = z.object({
  id: z.number().int(),
  email: z.string(),
  auctionAccess: z.boolean(),
  instructionShowed: z.boolean(),
  createdAt: z.string(),
  profile: z.object({
    phone: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    patronymic: z.string().optional(),
    avatar: z
      .object({
        id: z.number().int(),
        name: z.string(),
        disk: z.string(),
        extension: z.string(),
        size: z.number().int(),
        absolutePath: z.string(),
        type: z.string(),
        isActive: z.boolean(),
        createdAt: z.string(),
      })
      .optional(),
  }),
  role: z.object({
    name: z.string(),
    displayName: z.string(),
    permissions: z.array(z.string()),
  }),
  company: z.object({
    createdAt: z.string(),
    description: z.string().nullable(),
    id: z.number().int(),
    isActive: z.boolean(),
    isSurfer: z.boolean(),
    name: z.string(),
    nif: z.string().nullable(),
    nccVerified: z.boolean().nullish(),
    city: z
      .object({
        id: z.number().int(),
        name: z.string(),
      })
      .nullable(),
    type: z.string().nullable(),
    updatedAt: z.string(),
    activitySpheres: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
      })
    ),
    manager: z.object({
      id: z.number(),
      email: z.string(),
      isActive: z.boolean(),
      profile: z.object({
        id: z.number(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        phone: z.string().nullable(),
        position: z.string().nullable(),
      }),
    }),
    rating: z.union([z.number(), z.string()]).nullable().optional(),
    level: z.string().nullable().optional(),
  }),
});

export type SignInResponse = z.infer<typeof $SignInResponse>;
export const $SignInResponse = z.object({
  data: $TokensData,
  meta: z.object({
    user: $User,
  }),
});

export type RecoveryPasswordRequest = z.infer<typeof $RecoveryPassword>;
export const $RecoveryPassword = z.object({
  email: z.string(),
});

export type RecoveryPasswordResponse = z.infer<
  typeof $RecoveryPasswordResponse
>;
export const $RecoveryPasswordResponse = z.object({
  data: z.null(),
});

export type ResetPasswordRequest = z.infer<typeof $ResetPasswordRequest>;
export const $ResetPasswordRequest = z.object({
  token: z.string(),
  password: z.string(),
  passwordConfirmation: z.string(),
});

export type ResetPasswordResponse = z.infer<typeof $ResetPasswordResponse>;
export const $ResetPasswordResponse = z.object({
  data: z.null(),
});

export type RegisterRequest = z.infer<typeof $RegisterRequest>;
export const $RegisterRequest = z.object({
  email: z.string(),
  password: z.string(),
  password_confirmation: z.string(),
  profile: z
    .object({
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      phone: z.string().nullish(),
    })
    .nullish(),
  company: z.object({
    name: z.string(),
    type: z.string().nullish(),
    nif: z.string().nullish(),
    cityId: z.number().int().nullish(),
    description: z.string().nullish(),
    isActive: z.boolean().optional(),
  }),
});

export type RegisterResponse = z.infer<typeof $RegisterResponse>;
export const $RegisterResponse = z.object({
  data: $TokensData,
  meta: z.object({
    user: $User,
  }),
});

export type VerifyEmailRequest = z.infer<typeof $VerifyEmailRequest>;
export const $VerifyEmailRequest = z.object({
  token: z.string(),
});

export type VerifyEmailResponse = z.infer<typeof $VerifyEmailResponse>;
export const $VerifyEmailResponse = z.object({
  data: z.object({
    tokenType: z.string(),
    expiresIn: z.number(),
    accessToken: z.string(),
    refreshToken: z.string(),
  }),
  meta: z.object({
    user: z.object({
      id: z.number().int(),
      email: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      role: z.string().nullable(),
      profile: z.object({
        firstName: z.string(),
        lastName: z.string(),
        phone: z.string().nullable(),
      }),
    }),
  }),
});

export type SurferRegisterRequest = z.infer<typeof $SurferRegisterRequest>;
export const $SurferRegisterRequest = z.object({
  email: z.string(),
  password: z.string(),
  password_confirmation: z.string(),
  profile: z
    .object({
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      phone: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  company: z.object({
    name: z.string(),
    ncc: z.string().optional(),
  }),
});

export type SurferRegisterResponse = z.infer<typeof $SurferRegisterResponse>;
export const $SurferRegisterResponse = z.object({
  data: $TokensData,
  meta: z.object({
    user: $User,
  }),
});

export const $RegistrationPreflightRequest = $RegisterRequest.extend({
  acceptTerms: z.boolean(),
  verificationId: z.string().optional(),
});
export type RegistrationPreflightRequest = z.infer<
  typeof $RegistrationPreflightRequest
>;

export const $RegistrationResendRequest = $RegistrationPreflightRequest.extend({
  verificationId: z.string(),
});
export type RegistrationResendRequest = z.infer<
  typeof $RegistrationResendRequest
>;

export const $VerifiedRegistrationRequest =
  $RegistrationPreflightRequest.extend({
    verificationId: z.string(),
    code: z.string().regex(/^[0-9]{6}$/),
  });
export type VerifiedRegistrationRequest = z.infer<
  typeof $VerifiedRegistrationRequest
>;

// Responses without meta are unwrapped by the existing Axios interceptor.
export const $RegistrationVerification = z.object({
  verificationId: z.string(),
  email: z.string(),
  delivery: z.enum(['queued', 'reused']),
  expiresAt: z.string(),
  resendAvailableAt: z.string(),
});
export type RegistrationVerification = z.infer<
  typeof $RegistrationVerification
>;

export const $RegistrationErrorData = z.object({
  category: z.enum(['form', 'account', 'code', 'rate_limit', 'technical']),
  code: z.string(),
  errorCodes: z.record(z.array(z.string())).optional(),
  verificationReset: z.boolean().optional(),
  resendAvailableAt: z.string().optional(),
  attemptsRemaining: z.number().optional(),
});
export type RegistrationErrorData = z.infer<typeof $RegistrationErrorData>;

export const $SurferRegistrationPreflightRequest =
  $SurferRegisterRequest.extend({
    acceptTerms: z.boolean(),
    verificationId: z.string().optional(),
  });
export type SurferRegistrationPreflightRequest = z.infer<
  typeof $SurferRegistrationPreflightRequest
>;

export const $SurferRegistrationResendRequest =
  $SurferRegistrationPreflightRequest.extend({
    verificationId: z.string(),
  });
export type SurferRegistrationResendRequest = z.infer<
  typeof $SurferRegistrationResendRequest
>;

export const $VerifiedSurferRegistrationRequest =
  $SurferRegistrationPreflightRequest.extend({
    verificationId: z.string(),
    code: z.string().regex(/^[0-9]{6}$/),
  });
export type VerifiedSurferRegistrationRequest = z.infer<
  typeof $VerifiedSurferRegistrationRequest
>;
